import React, { useState, useMemo, ChangeEvent } from "react";
import {
  IconButton,
  DialogTitle,
  Dialog,
  CircularProgress,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  useUpdateClientFileUpload,
  useGetClients,
  useUpdateClientFileChunksBatchUpload,
  useUpdateClientResearchFileChunksBatchUpload,
} from "../../hooks";
import ResearchExperimentsForm from "../forms/ResearchExperimentsForm";
import { colors } from "../../../assets/styles/theme";

import dayjs, { Dayjs } from "dayjs";

interface FileUploadProps {
  openFileUpload: any;
  uploadType: any;
  handleSave?: (file: any) => void;
  handlePopUpClose?: () => void;
  handleUploadCancel?: () => void;
  initialFileValues: any;
  editData: any;
  isEditMode: any;
  industries: any;
  fromScreen: any;
}

const FileUpload: React.FC<FileUploadProps> = ({
  openFileUpload,
  handleSave,
  uploadType,
  handlePopUpClose,
  handleUploadCancel,
  initialFileValues,
  editData,
  isEditMode,
  industries,
  fromScreen,
}) => {
  console.log("editData file upload", editData);

  const [error, setError] = useState<any | null>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const [success, setSuccess] = useState<any | null>(false);
  // modal popup
  const [fullWidth, setFullWidth] = React.useState(true);

  const { mutate: uploadClientFile, isPending: isUploadClientFilePending } =
    useUpdateClientFileUpload();

  const {
    mutate: uploadClientFileChunksBatch,
    isPending: isUploadClientFileChunksBatchPending,
  } = useUpdateClientFileChunksBatchUpload();

  const {
    mutate: uploadClientResearchFileChunksBatch,
    isPending: isUploadClientResearchFileChunksBatchPending,
  } = useUpdateClientResearchFileChunksBatchUpload();

  const {
    data: getClientsData,
    isLoading: isGetClientsDataPending,
    error: getClientsError,
  } = useGetClients();

  console.log("getClientsData", getClientsData);

  const getClientsDataForDropdown = (data: any) => {
    const uniqueSet = new Set();
    const result: any = [];

    data?.forEach((row: any) => {
      const clientName = row.clientName;
      const clientId = row.id;

      // Check if the clientName is already in the set
      if (!uniqueSet.has(clientName)) {
        uniqueSet.add(clientName);
        result.push({ label: clientName, value: clientId });
      }
    });

    result.sort((a: any, b: any) => a.label.localeCompare(b.label));
    return result;
  };

  const getUniqueValues = (columnKey: any, data: any) => {
    const uniqueSet = new Set();
    data?.forEach((row: any) => {
      if (
        columnKey === "parentAccountDetails" &&
        Array.isArray(row[columnKey])
      ) {
        // Extract names from parentAccountDetails array
        row[columnKey]?.forEach((account) => {
          if (account?.name) {
            uniqueSet.add(account.name);
          }
        });
      } else if (columnKey === "tapClicksId") {
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          uniqueSet.add(parseInt(row[columnKey]));
        }
      } else if (
        columnKey === "lastUpdatedDate" ||
        columnKey === "createdDate"
      ) {
        // For date fields, standardize the format before adding to set
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          const date = new Date(row[columnKey]);
          if (!isNaN(date.getTime())) {
            // Convert to consistent format
            const standardDate = date.toLocaleDateString();
            uniqueSet.add(standardDate);
          }
        }
      } else {
        // Handle regular columns
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          uniqueSet.add(row[columnKey]);
        }
      }
    });
    // Convert set to array and sort
    const sortedArray = Array.from(uniqueSet);
    // Sort based on column type
    if (columnKey === "lastUpdatedDate" || columnKey === "createdDate") {
      return sortedArray.sort(
        (a: any, b: any) => new Date(a).getTime() - new Date(b).getTime()
      );
    } else if (columnKey === "tapClicksId") {
      return sortedArray.sort((a: any, b: any) => a - b);
    } else {
      return sortedArray.sort();
    }
  };

  function getFileNames(files: any) {
    console.log("files",files)
    return files
    .filter((item: any) => item.isDelete === false)
    .map((item: any) => item.file.name)
    .join(",");
  }

  const handleFileSave = (files: any, values: any, id: any, batchId: any) => {
    console.log("values", values, files, id, batchId);
    console.log("uploadType", uploadType);

    const reqBody = {
      id: values.client.value,
      body: {
        uploadTypeId: uploadType == "research" ? 2 : 1,
        batchNumber: batchId,
      },
    };

    const parsedStartDate =
      values?.testStartDate && dayjs(values?.testStartDate);
    const parsedEndDate = values?.testEndDate && dayjs(values?.testEndDate);

    const experimentAndResearch = {
      experimentId: id || 0,
      title: values?.title || "",
      industry: values?.industry || "",
      testAndLearnVehicle: values?.testAndLearnVehicle || "",
      hypothesis: values?.hypothesis || "",
      background: values?.background || "",
      testStoresOrGeography: values?.testStoresOrGeography || "",
      controlStoresOrGeography: values?.controlStoresOrGeography || "",
      measuredKPI: values?.measuredKPI || "",
      testDesign: values?.testDesign || "",
      testStartDate: values?.testStartDate
        ? parsedStartDate.format("MM/DD/YYYY")
        : null,
      testEndDate: values?.testEndDate
        ? parsedEndDate.format("MM/DD/YYYY")
        : null,
      results: values?.results || "",
      statisticalSignificance: values?.statisticalSignificance || "",
      recommendationsOrNotes: values?.recommendationsOrNotes || "",
      uploadTypeId: uploadType == "research" ? 2 : 1,
      batchNumber: batchId,
    };

    const reqBodyResearch = {
      id: values.client.value,
      body: experimentAndResearch,
    };

    let payload = uploadType == "research" ? reqBodyResearch : reqBody;

    console.log("onSavePress reqBody:", payload);

    console.log('getFileNames', getFileNames(files))

    const fileName =
      uploadType == "research" ? values?.title : (getFileNames(files) || "File");

    let api =
      uploadType == "research"
        ? uploadClientResearchFileChunksBatch
        : uploadClientFileChunksBatch;

    api(payload, {
      onSuccess: (res: any) => onUploadClientFileSuccess(res, fileName),
      onError: onUploadClientFileError,
    });
  };

  const handleFileSave1 = (files: any, values: any, id: any, batchId: any) => {
    console.log("values", values, files, id, batchId);

    setError(false);
    setErrorMessage("");

    let updatedFiles = files.filter(
      (file: any) => !(file.isDelete === true && file.isNew === true)
    );

    let finalFiles = updatedFiles.filter(
      (file: any) => !(file.isNew === false && file.isDelete === false)
    );

    console.log("finalFiles", finalFiles);
    // Transform the files into the desired structure
    const transformedFiles = Array.from(finalFiles).map((file: any) => ({
      media: file?.base64,
      fileName: file?.file?.name,
      mimeType: file?.file?.type,
      isSensitive: file?.isSensitive,
      isDelete: file?.isDelete,
      FileId: file?.id || 0,
    }));

    const parsedStartDate =
      values?.testStartDate && dayjs(values?.testStartDate);
    const parsedEndDate = values?.testEndDate && dayjs(values?.testEndDate);

    const experimentAndResearch = {
      experimentId: id || 0,
      title: values?.title || "",
      industry: values?.industry || "",
      testAndLearnVehicle: values?.testAndLearnVehicle || "",
      hypothesis: values?.hypothesis || "",
      background: values?.background || "",
      testStoresOrGeography: values?.testStoresOrGeography || "",
      controlStoresOrGeography: values?.controlStoresOrGeography || "",
      measuredKPI: values?.measuredKPI || "",
      testDesign: values?.testDesign || "",
      testStartDate: values?.testStartDate
        ? parsedStartDate.format("MM/DD/YYYY")
        : null,
      testEndDate: values?.testEndDate
        ? parsedEndDate.format("MM/DD/YYYY")
        : null,
      results: values?.results || "",
      statisticalSignificance: values?.statisticalSignificance || "",
      recommendationsOrNotes: values?.recommendationsOrNotes || "",
    };

    const reqBody = {
      id: values.client.value,
      body: {
        uploadType: uploadType == "research" ? 2 : 1,
        files: transformedFiles,
        experimentAndResearch: experimentAndResearch,
      },
    };

    console.log(transformedFiles);
    console.log("onSavePress reqBody:", reqBody);

    const newFiles = finalFiles.filter((file: any) => file.isNew);
    const fileName =
      uploadType == "research"
        ? values?.title
        : getFileNames(newFiles) || "File";

    uploadClientFile(reqBody, {
      onSuccess: (res) => onUploadClientFileSuccess(res, fileName),
      onError: onUploadClientFileError,
    });
  };

  const onUploadClientFileSuccess = (res: any, fileName: any) => {
    console.log("res", res);
    handleSave!(fileName);
  };

  const onUploadClientFileError = (error: any) => {
    console.log("error", error.data);
    setError(true);
    setErrorMessage(error?.data?.message || error?.data?.title);
  };

  return (
    <>
      <Dialog
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
        }}
        slotProps={{
          backdrop: {
            onClick: (e) => e.stopPropagation(),
          },
        }}
        aria-labelledby="Upload-file-dialog-title"
        open={openFileUpload}
        fullWidth={fullWidth}
        // maxWidth={maxWidth}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "990px",
          },
          "& .MuiDialogContent-root": {
            padding: 2,
          },
          "& .MuiDialogActions-root": {
            padding: 1,
          },
        }}
        className="digitalModal"
      >
        <DialogTitle sx={{ m: 0 }} id="Upload-file-dialog-title">
          {uploadType == "research" ? (
            <>Test and Learn Insights Form</>
          ) : (
            <>FILE UPLOAD</>
          )}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePopUpClose}
          sx={(theme: any) => ({
            position: "absolute",
            right: 8,
            top: 12,
            // color: theme.palette.grey[650],
            color: colors.lightBlack,
          })}
        >
          <CloseIcon sx={{ fontSize: "2rem" }} />
        </IconButton>

        {!isGetClientsDataPending && (
          <ResearchExperimentsForm
            handleSave={handleFileSave}
            uploadType={uploadType}
            clients={getClientsDataForDropdown(getClientsData?.data)}
            apiErrorText={errorMessage}
            apiError={error}
            industriesDataOptions={
              fromScreen == "ManagePlatform"
                ? getClientsData?.data
                : getClientsData?.data
            }
            handleUploadCancel={handlePopUpClose}
            isLoading={isUploadClientFileChunksBatchPending || isUploadClientResearchFileChunksBatchPending}
            initialFileValues={initialFileValues}
            isEditMode={isEditMode}
            editData={editData}
            fromScreenData={fromScreen}
          />
        )}

        {isGetClientsDataPending && (
          <Box sx={{ minHeight: "150px" }}>
            <CircularProgress
              color="primary"
              size="1.5rem"
              sx={{ position: "absolute", left: "50%", top: "45%" }}
            />
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default FileUpload;
